import { useState } from 'react';
import type { FC, ReactElement } from 'react';
import { Box, Flex } from '@chakra-ui/react';
import { GetIcon } from '../../components/icons';
import type { IconName } from '../../components/icons/Icon.map';
import type { IconSize } from '../../components/icons/types';

export interface RatingProps {
    initialRating: number;
    disabled?: boolean;
    iconName?: IconName;
    emptyIconName?: IconName;
    halfEmptyStar?: IconName;
    iconSize?: IconSize;
    onRatingChanged?: (rating: number) => void;
    cursor?: string;
    id?: string;
}

export const Rating: FC<RatingProps> = ({
    id,
    initialRating = 5,
    disabled = false,
    iconName = 'star',
    emptyIconName = 'empty-star',
    halfEmptyStar = 'half-empty-star',
    iconSize = 'md',
    cursor,
    onRatingChanged,
}) => {
    const stars = Array.from({ length: 5 });
    const [rating, setRating] = useState<number>(initialRating);

    const handleRatingChange = (updatedRating: number): void => {
        if (!disabled) {
            setRating(updatedRating);

            if (onRatingChanged) {
                onRatingChanged(updatedRating);
            }
        }
    };

    const getStarIcon = (index: number): ReactElement => {
        const currentStar = index + 1;
        if (currentStar <= rating) {
            return <GetIcon name={iconName} size={iconSize} cursor={cursor ?? 'pointer'} />;
        }

        if (currentStar - rating > 0 && Math.abs(currentStar - rating) < 1) {
            return <GetIcon name={halfEmptyStar} size={iconSize} cursor={cursor ?? 'pointer'} />;
        }

        return <GetIcon name={emptyIconName} size={iconSize} cursor={cursor ?? 'pointer'} />;
    };

    return (
        <Flex data-testid={id} className="space-x-1">
            {stars.map((_, index) => (
                <Box
                    display="inline-flex"
                    // eslint-disable-next-line react/no-array-index-key
                    key={index}
                    onClick={() => handleRatingChange(index + 1)}
                    cursor={(cursor ?? disabled) ? 'auto' : 'pointer'}
                >
                    {getStarIcon(index)}
                </Box>
            ))}
        </Flex>
    );
};

Rating.defaultProps = {
    disabled: undefined,
    iconName: undefined,
    emptyIconName: undefined,
    halfEmptyStar: undefined,
    iconSize: undefined,
    onRatingChanged: undefined,
    cursor: undefined,
    id: undefined,
};
